/**
 * `useFollowUps(ref)` (13.2-07; D-13.2-A-4, D-13.2-P-7): the follow-ups of one incident, read from the Action
 * Register, each with its state (`followUpsOf`, 13.2-06). The Follow-ups panel shows them, and 13.2-08 feeds them to
 * `incidentChecklist` → `openChecks` for the closure gate.
 *
 * **The read.** The AR is small (295 rows on 25 Oct 2026), and `SourceRef` / `ParentId` are not known to be indexed
 * (an unindexed `$filter` would need the "may fail randomly" Prefer header). So the live source makes ONE paged read
 * of the whole register (`listActionRows`, the read Today, Verify and Release already make) and links client-side
 * with `followUpsOf`. That read is shared: every `useFollowUps` on the page (the panel and, from 13.2-08, the closure
 * panel) reuses one cached read per Graph context for up to `FOLLOW_UPS_CACHE_MS`.
 *
 * **Refetch.** `reload()` drops the cached read and tells every mounted `useFollowUps` to read again, so a follow-up
 * added in the panel shows in the closure checks too. The panel calls it after every create.
 *
 * **Practice records** (`INC-P<n>`) have no Action Register rows: the live source answers `[]` without a read.
 * The dev previews supply their own source through `FollowUpsSourceContext`.
 *
 * The return shape (13.2-08 relies on it):
 *   `{ status: 'loading' | 'ready' | 'error', followUps: FollowUp[], error: string | null, refreshing: boolean,
 *      reload: () => void }`
 * `followUps` is `[]` until the first read lands and is never a partial list: while `status` is `loading` or `error`,
 * a caller must treat the follow-ups as unknown (13.2-05: pass no `openChecks`, so the approve gate refuses
 * `checks-unknown`). `refreshing` is true while a re-read runs over a ready list (the old list stays shown).
 */

import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { followUpsOf } from '../lib/followUps';
import type { FollowUp } from '../lib/followUps';
import { isPracticeRef } from '../lib/recordQuery';
import { listActionRows } from '../services/actionService';
import type { ActionIO } from '../services/actionService';
import type { ActionRow } from '../types/actionRegister';
import { useActionRuntime } from './useActionRuntime';

/** How long one AR read is shared between the hooks on a page (a create or `reload()` drops it at once). */
export const FOLLOW_UPS_CACHE_MS = 60_000;

/** Where the follow-ups come from: the Action Register in the app, synthetic rows in the previews. */
export interface FollowUpsSource {
  /** The follow-ups of incident `ref`, each with its state as of `now`. */
  read: (ref: string, now: Date) => Promise<FollowUp[]>;
  /** Forget any cached read, so the next `read` is fresh. */
  invalidate?: () => void;
}

/** A preview (or a test) may supply its own source. The app leaves it unset: the live source is used. */
export const FollowUpsSourceContext = createContext<FollowUpsSource | null>(null);

export interface FollowUpsResult {
  status: 'loading' | 'ready' | 'error';
  /** `followUpsOf(ref, rows, now)`: Retired and NotMine left out, by due day, each with a state. */
  followUps: FollowUp[];
  error: string | null;
  /** A re-read is running over a list that is already shown. */
  refreshing: boolean;
  /** Read again, fresh, in every `useFollowUps` on the page. */
  reload: () => void;
}

export interface UseFollowUpsOptions {
  /** The clock the states are computed against (tests pin it). Default `new Date()`. */
  now?: () => Date;
}

// ---------------------------------------------------------------------------
// The live source: one shared, cached AR read per Graph context
// ---------------------------------------------------------------------------

const liveSources = new WeakMap<ActionIO, FollowUpsSource>();

/** The live source over `io` (one per `io`, so its cache is shared by every hook on the page). */
export function liveFollowUpsSource(io: ActionIO, clock: () => number = Date.now): FollowUpsSource {
  const known = liveSources.get(io);
  if (known) return known;
  let cached: { at: number; rows: Promise<ActionRow[]> } | null = null;
  const rows = (): Promise<ActionRow[]> => {
    if (cached && clock() - cached.at < FOLLOW_UPS_CACHE_MS) return cached.rows;
    const entry = { at: clock(), rows: listActionRows(io) };
    cached = entry;
    entry.rows.catch(() => {
      if (cached === entry) cached = null;
    });
    return entry.rows;
  };
  const source: FollowUpsSource = {
    read: async (ref, now) => (isPracticeRef(ref) ? [] : followUpsOf(ref, await rows(), now)),
    invalidate: () => {
      cached = null;
    },
  };
  liveSources.set(io, source);
  return source;
}

/** Every mounted hook's "read again". */
const listeners = new Set<() => void>();

/** Drop `source`'s cached read and make every mounted `useFollowUps` read again. */
export function refreshFollowUps(source: FollowUpsSource | null): void {
  source?.invalidate?.();
  for (const l of [...listeners]) l();
}

// ---------------------------------------------------------------------------
// The hook
// ---------------------------------------------------------------------------

/** The last read that landed: for which ref, and which "read again" (`tick`) it answered. */
interface ReadState {
  key: string;
  tick: number;
  status: 'ready' | 'error';
  followUps: FollowUp[];
  error: string | null;
}

const EMPTY: FollowUp[] = [];

export function useFollowUps(ref: string | null, opts: UseFollowUpsOptions = {}): FollowUpsResult {
  const { io } = useActionRuntime();
  const given = useContext(FollowUpsSourceContext);
  const source = useMemo(() => given ?? (io ? liveFollowUpsSource(io) : null), [given, io]);
  const [tick, setTick] = useState(0);
  const [state, setState] = useState<ReadState | null>(null);
  // Held in a ref, so an inline `now` doesn't start a read on every render.
  const nowRef = useRef(opts.now);
  useEffect(() => {
    nowRef.current = opts.now;
  });

  useEffect(() => {
    const bump = () => setTick((n) => n + 1);
    listeners.add(bump);
    return () => {
      listeners.delete(bump);
    };
  }, []);

  const key = ref ?? '';
  useEffect(() => {
    if (!source || !ref) return;
    let cancelled = false;
    source
      .read(ref, nowRef.current ? nowRef.current() : new Date())
      .then((followUps) => {
        if (!cancelled) setState({ key, tick, status: 'ready', followUps, error: null });
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        setState({ key, tick, status: 'error', followUps: EMPTY, error: err instanceof Error ? err.message : String(err) });
      });
    return () => {
      cancelled = true;
    };
  }, [source, ref, key, tick]);

  const reload = useCallback(() => refreshFollowUps(source), [source]);

  // Loading and refreshing are derived, never set in the effect: a ready list stays shown while a re-read runs.
  return useMemo<FollowUpsResult>(() => {
    const loading: FollowUpsResult = { status: 'loading', followUps: EMPTY, error: null, refreshing: false, reload };
    if (!state || state.key !== key) return loading;
    const stale = state.tick !== tick;
    if (state.status === 'error') return stale ? loading : { ...loading, status: 'error', error: state.error };
    return { status: 'ready', followUps: state.followUps, error: null, refreshing: stale, reload };
  }, [state, key, tick, reload]);
}
